import { Button } from '@aecom-ai-engineering/react-components';
import { useBlobsById, useCopyToClipboard, usePackageId, useProjectId } from '@consigli/hooks';
import { convertBlobToDocument } from '@consigli/types';
import { uniqueBy } from '@consigli/utils';
import { clsx } from 'clsx';
import { Check, Copy, Info } from 'lucide-react';
import { FC, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';

import { AgentChatLogo } from '@/components/agent-chat-logo';
import { FeatureFlag, useFeatureFlag } from '@/feature-flags';
import { useViewer } from '@/features/document-viewer/use-viewer';
import 'react-tooltip/dist/react-tooltip.css';
import { Message } from '@/util/interfaces';

import { MarkdownBody } from './markdown-body';
import { PropChatThinkingIndicator } from './prop-chat-thinking-indicator';
import { SourceList } from './source-list';
import { type CitedFinding, countSources } from './sources';
import { SourcesMenu } from './sources-menu';
import { SourcesModal } from './sources-modal';
import { StepIndicator } from './step-indicator';
import { type PropChatVariant } from './types';
import { useMinimumActiveTime } from './use-minimum-active-time';

type ChatMessageProps = {
  from?: 'SYSTEM' | 'USER';
  variant: PropChatVariant;
} & (
  | {
      loading?: false;
      message: Message;
    }
  | {
      loading: true;
      message?: Message;
    }
);

export const ChatMessage: FC<ChatMessageProps> = ({ message, loading, from = 'USER', variant }) => {
  const { t } = useTranslation();
  const infoTooltipId = useId();
  const copyTooltipId = useId();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { blobsById } = useBlobsById(projectId, packageId);
  const { openStandaloneDocument } = useViewer();
  const { copied, copyToClipboard } = useCopyToClipboard();
  const [isInfoActive, setIsInfoActive] = useState(false);
  const [isSourcesOpen, setIsSourcesOpen] = useState(false);
  const isContextEnabled = useFeatureFlag(FeatureFlag.PropChatContext);
  const { steps: shownSteps, holding: holdingStep } = useMinimumActiveTime(message?.steps ?? []);

  const isSystemMessage = useMemo(() => from === 'SYSTEM', [from]);
  const isFullscreen = variant === 'fullscreen';
  const sources = (!loading && message.sources) || [];
  const sourceDocuments = uniqueBy(
    sources.filter((source) => source.blobId !== undefined),
    (source) => source.blobId,
  ).flatMap((source) => {
    const blob = blobsById[source.blobId as string];
    return blob ? [blob] : [];
  });
  // Findings ship with add-context, so with it switched off only documents count.
  const sourceFindings: CitedFinding[] = isContextEnabled
    ? uniqueBy(
        sources.filter((source) => source.findingId !== undefined),
        (source) => source.findingId,
      ).map(({ findingId, conflictType }) => ({ findingId: findingId as number, conflictType }))
    : [];
  // From the citations rather than resolved findings, so naming the row costs no
  // request - only opening the list does.
  const sourceCounts = countSources(sourceDocuments.length, sourceFindings);
  const hasSources = sourceDocuments.length + sourceFindings.length > 0;
  const showsSourceList = isInfoActive && hasSources;
  const messageActions =
    !loading && isSystemMessage ? (
      <div className="flex w-full items-center gap-[2px] text-[length:var(--ds-typography-paragraph-mini-font-size)] text-[var(--ds-color-text-secondary)]">
        <Button
          variant="ghost"
          size="mini"
          className={clsx(
            'size-[var(--ds-space-5)] min-h-0 shrink-0 rounded-[var(--ds-radius-4)] p-[var(--ds-space-1)]',
            isInfoActive && 'bg-[var(--ds-color-bg-secondary-dark)]',
          )}
          data-tooltip-id={infoTooltipId}
          aria-label={t('prop-chat.message-info')}
          onClick={() => setIsInfoActive(true)}
        >
          <Info aria-hidden="true" className="size-[var(--ds-space-3)]" />
        </Button>
        <Tooltip
          className={clsx(
            '!z-50',
            showsSourceList
              ? '!w-[208px] !max-w-[208px] !overflow-hidden !rounded-[var(--ds-radius-8)] !bg-transparent !p-0 !opacity-100'
              : clsx(
                  // The tooltip renders inside the drawer, which clips its
                  // overflow, so a fixed 500px cap was cut off at the drawer's
                  // edge. Track the drawer width instead so it stays readable
                  // at any size the user drags it to.
                  isFullscreen
                    ? '!max-w-[500px]'
                    : '!max-w-[calc(var(--agent-chat-width)-var(--ds-space-8))]',
                  '!break-words !rounded-[var(--ds-radius-8)] !bg-[var(--ds-color-bg-dark)] !px-[var(--ds-space-2)] !py-[var(--ds-space-1-5)] !font-[var(--ds-typography-font-definitions-font-family-body)] !text-[length:var(--ds-typography-paragraph-13-regular-font-size)] !font-[var(--ds-typography-paragraph-13-regular-font-weight)] !leading-[var(--ds-typography-paragraph-13-regular-line-height)] !tracking-[var(--ds-typography-paragraph-13-regular-letter-spacing)] !text-[var(--ds-color-text-invert)]',
                ),
          )}
          id={infoTooltipId}
          clickable
          noArrow={showsSourceList}
          place="bottom-start"
          offset={4}
          openEvents={{ mouseenter: true, focus: true, click: true }}
          closeEvents={{ mouseleave: true, blur: true }}
          globalCloseEvents={{ escape: true, clickOutsideAnchor: true }}
          afterHide={() => setIsInfoActive(false)}
        >
          {!isInfoActive ? (
            t('prop-chat.message-info')
          ) : !hasSources ? (
            t('prop-chat.no-source-documents')
          ) : isContextEnabled ? (
            <SourcesMenu counts={sourceCounts} onOpenList={() => setIsSourcesOpen(true)} />
          ) : (
            /* The list in the tooltip itself, as it was before context. */
            <SourceList
              sources={sourceDocuments}
              onSourceClick={(blob) => openStandaloneDocument(convertBlobToDocument(blob))}
            />
          )}
        </Tooltip>
        {isContextEnabled && isSourcesOpen && hasSources && (
          <SourcesModal
            documents={sourceDocuments}
            findings={sourceFindings}
            onClose={() => setIsSourcesOpen(false)}
            onDocumentClick={(blob) => openStandaloneDocument(convertBlobToDocument(blob))}
          />
        )}
        <Button
          variant="ghost"
          size="mini"
          className={clsx(
            'size-[var(--ds-space-5)] min-h-0 shrink-0 rounded-[var(--ds-radius-4)] p-[var(--ds-space-1)]',
            copied && 'bg-[var(--ds-color-bg-secondary-dark)]',
          )}
          data-tooltip-id={copyTooltipId}
          aria-label={t('prop-chat.copy-message')}
          onClick={() => void copyToClipboard(message.body)}
        >
          {copied ? (
            <Check aria-hidden="true" className="size-[var(--ds-space-3)]" />
          ) : (
            <Copy aria-hidden="true" className="size-[var(--ds-space-3)]" />
          )}
        </Button>
        <Tooltip
          className="!z-50 !rounded-[var(--ds-radius-8)] !bg-[var(--ds-color-bg-dark)] !px-[var(--ds-space-2)] !py-[var(--ds-space-1-5)] !font-[var(--ds-typography-font-definitions-font-family-body)] !text-[length:var(--ds-typography-paragraph-13-regular-font-size)] !font-[var(--ds-typography-paragraph-13-regular-font-weight)] !leading-[var(--ds-typography-paragraph-13-regular-line-height)] !tracking-[var(--ds-typography-paragraph-13-regular-letter-spacing)] !text-[var(--ds-color-text-invert)]"
          id={copyTooltipId}
          place="left"
          openEvents={{ mouseenter: true, focus: true, click: true }}
          closeEvents={{ mouseleave: true, blur: true }}
          globalCloseEvents={{ escape: true, clickOutsideAnchor: true }}
        >
          {copied ? t('prop-chat.message-copied') : t('prop-chat.copy-tooltip')}
        </Tooltip>
        <span className="sr-only" role="status" aria-live="polite">
          {copied ? t('prop-chat.message-copied') : ''}
        </span>
      </div>
    ) : null;

  if (!isSystemMessage) {
    if (!message) return null;

    return (
      <div className="flex w-full justify-end">
        <div className="max-w-full rounded-bl-[var(--ds-radius-8)] rounded-br-[var(--ds-radius-8)] rounded-tl-[var(--ds-radius-8)] bg-[var(--ds-color-bg-secondary)] px-[var(--ds-space-2-5)] py-[var(--ds-space-2)] font-[var(--ds-typography-font-definitions-font-family-body)] text-[length:var(--ds-typography-paragraph-13-regular-font-size)] font-[var(--ds-typography-paragraph-13-regular-font-weight)] leading-[var(--ds-typography-paragraph-13-regular-line-height)] tracking-[var(--ds-typography-paragraph-13-regular-letter-spacing)] text-[var(--ds-color-text-secondary)]">
          <span className="sr-only">{t('prop-chat.you')}: </span>
          {/* The user put those line breaks in with Shift+Enter. */}
          <MarkdownBody content={message.body} preserveLineBreaks />
        </div>
      </div>
    );
  }

  // The wait runs from the moment the question is sent until the first of the
  // answer is on screen, so the indicator stands in for the message header for
  // that whole stretch - steps arriving underneath it do not end it. It carries
  // its own agent mark, so the header would be a second copy of the same logo.
  //
  // It also outlasts `loading` by however long a step is still being held: the
  // backend closes the last step in the same breath as the first chunk of the
  // answer, so without this the answer would replace the list before that
  // step's spinner had drawn a single frame.
  if (loading || holdingStep) {
    return (
      <div className="flex w-full flex-col gap-[var(--ds-space-2)]">
        <PropChatThinkingIndicator />
        {shownSteps.length ? <StepIndicator steps={shownSteps} /> : null}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-[var(--ds-space-2)]">
      <div className="flex w-full items-center gap-[var(--ds-space-2)]">
        <AgentChatLogo className="size-[var(--ds-space-6)]" />
        <span className="min-w-0 flex-1 font-[var(--ds-typography-font-definitions-font-family-body)] text-[length:var(--ds-typography-paragraph-13-regular-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-paragraph-mini-line-height)] tracking-[var(--ds-typography-paragraph-mini-letter-spacing)] text-[var(--ds-color-text-secondary)]">
          {t('prop-chat.agent-name')}
        </span>
      </div>
      {shownSteps.length ? (
        <StepIndicator steps={shownSteps} thinkingSeconds={message.thinkingSeconds} />
      ) : null}
      <div className="w-full rounded-[var(--ds-radius-8)] bg-[var(--ds-color-bg-brand-light)] p-[var(--ds-space-3)] font-[var(--ds-typography-font-definitions-font-family-body)] text-[length:var(--ds-typography-paragraph-13-regular-font-size)] font-[var(--ds-typography-paragraph-13-regular-font-weight)] leading-[var(--ds-typography-paragraph-13-regular-line-height)] tracking-[var(--ds-typography-paragraph-13-regular-letter-spacing)] text-[var(--ds-color-text-primary)]">
        <MarkdownBody content={message.body} />
      </div>
      {messageActions}
    </div>
  );
};
